import type { ChannelFilters, SourceKey } from '../types';

const CHANNEL_KEY = 'calendar.channels.v1';
const SOURCES_KEY = 'calendar.sources.v1';
const RECENT_KEY  = 'calendar.recent_athletes.v1';
export const RECENT_ATHLETES_LIMIT = 5;

export const DEFAULT_ENABLED_SOURCES: SourceKey[] = [
  'garmin', 'whoop', 'catapult', 'hawkin',
  'coros', 'suunto', 'oura',
];

const VALID_SOURCES = new Set<SourceKey>(DEFAULT_ENABLED_SOURCES);

function isSourceKeyArray(x: unknown): x is SourceKey[] {
  return Array.isArray(x) && x.every((s): s is SourceKey =>
    typeof s === 'string' && VALID_SOURCES.has(s as SourceKey)
  );
}

function migrateLegacyChannelFilters(): SourceKey[] | null {
  try {
    const raw = localStorage.getItem(CHANNEL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const out = new Set<SourceKey>();
    if (parsed.training) {
      // Training rolls up: Garmin daily + Catapult sessions + all terra brands.
      out.add('garmin'); out.add('catapult');
      out.add('coros'); out.add('suunto'); out.add('oura');
    }
    if (parsed.recovery)   { out.add('whoop'); }
    if (parsed.assessment) { out.add('hawkin'); }
    localStorage.removeItem(CHANNEL_KEY);
    const arr = Array.from(out);
    localStorage.setItem(SOURCES_KEY, JSON.stringify(arr));
    return arr;
  } catch {
    return null;
  }
}

export function loadEnabledSources(): SourceKey[] {
  try {
    const raw = localStorage.getItem(SOURCES_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return isSourceKeyArray(parsed) ? parsed : DEFAULT_ENABLED_SOURCES;
    }
    const migrated = migrateLegacyChannelFilters();
    return migrated ?? DEFAULT_ENABLED_SOURCES;
  } catch {
    return DEFAULT_ENABLED_SOURCES;
  }
}

export function saveEnabledSources(value: SourceKey[]): void {
  const filtered = value.filter((s) => VALID_SOURCES.has(s));
  localStorage.setItem(SOURCES_KEY, JSON.stringify(filtered));
}

export const DEFAULT_CHANNEL_FILTERS: ChannelFilters = {
  training: true, assessment: true, recovery: true,
};

function isChannelFilters(x: unknown): x is ChannelFilters {
  return !!x && typeof x === 'object'
    && typeof (x as any).training === 'boolean'
    && typeof (x as any).assessment === 'boolean'
    && typeof (x as any).recovery === 'boolean';
}

export function loadChannelFilters(): ChannelFilters {
  try {
    const raw = localStorage.getItem(CHANNEL_KEY);
    if (!raw) return DEFAULT_CHANNEL_FILTERS;
    const parsed = JSON.parse(raw);
    return isChannelFilters(parsed) ? parsed : DEFAULT_CHANNEL_FILTERS;
  } catch {
    return DEFAULT_CHANNEL_FILTERS;
  }
}

export function saveChannelFilters(value: ChannelFilters): void {
  localStorage.setItem(CHANNEL_KEY, JSON.stringify(value));
}

function isNumberArray(x: unknown): x is number[] {
  return Array.isArray(x) && x.every((n) => typeof n === 'number');
}

export function loadRecentAthletes(): number[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return isNumberArray(parsed) ? parsed.slice(0, RECENT_ATHLETES_LIMIT) : [];
  } catch {
    return [];
  }
}

export function pushRecentAthlete(athleteId: number): void {
  const current = loadRecentAthletes().filter((id) => id !== athleteId);
  const next = [athleteId, ...current].slice(0, RECENT_ATHLETES_LIMIT);
  localStorage.setItem(RECENT_KEY, JSON.stringify(next));
}
